import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { WindArrowIcon } from '@wavetrak/theme';
import { round } from 'lodash';
import { getUserSettings } from '@surfline/web-common';
import { scaleLinear } from 'd3-scale';
import { useAppSelector } from 'stores/hooks';

interface Props {
  reading: {
    timestamp?: number | undefined;
    speed?: number | undefined;
    gust?: number | undefined;
    direction?: number | undefined;
  };
  hourReadingWidth: number;
  showDetails?: boolean;
  customArrowConfig?: {
    maxSize: number;
    minSize: number;
  } | null;
}

// all arrow sizes >= 25mph will be the same size
const UPPER_BOUND_ARROW_SIZE_MPH = 25;
const UPPER_BOUND_ARROW_SIZE_KPH = UPPER_BOUND_ARROW_SIZE_MPH * 1.60934;

const WindHourReading: React.FC<Props> = ({
  reading,
  hourReadingWidth,
  showDetails = true,
  customArrowConfig = null,
}) => {
  const settings = useAppSelector(getUserSettings);

  const domainUpperBound =
    settings?.units.windSpeed === 'KPH' ? UPPER_BOUND_ARROW_SIZE_KPH : UPPER_BOUND_ARROW_SIZE_MPH;

  // scaling arrow sizes linearly with bounds on (between witdths of 7 and 19)
  const arrowScale = scaleLinear()
    .domain([0, domainUpperBound])
    .range([customArrowConfig?.minSize || 7, customArrowConfig?.maxSize || 19])
    .clamp(true);

  return (
    <Stack
      direction="column"
      key={`hours: ${reading.timestamp}`}
      maxWidth={`${hourReadingWidth}px`}
      data-testid="wind-hour-reading"
    >
      <Box height="22px" width={`${hourReadingWidth}px`} textAlign="center">
        <WindArrowIcon
          size={arrowScale(round(reading.speed || 0))}
          rotation={(reading.direction || 0) - 180}
          key={reading.timestamp || 0}
        />
      </Box>
      {showDetails ? (
        <>
          <Typography variant="callout2" component="span" align="center">
            {round(reading.speed || 0)}
          </Typography>
          <Typography variant="caption2" component="span" color="text.secondary" align="center">
            {round(reading.gust || 0)}
          </Typography>
        </>
      ) : null}
    </Stack>
  );
};

export default WindHourReading;
